import { Component, Injector, OnInit } from '@angular/core';

import { catchError, combineLatest, filter, map, of, switchMap } from 'rxjs';

import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { getMonitoringDateQueryInputFolderOptions }
    from '../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { CurationAgentModel } from '../../models/curation-agent.model';
import { AgentScopeModel, applyAgentScope } from '../../models/agent-scope.util';
import { CurationAgentsService } from '../../services/curation-agents.service';

import { ActivatedRoute } from '@angular/router';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { FolderService } from '../../../Monitor/Core/services/folder.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { MediaSubscriptionService } from '../../../Monitor/Core/services/media-subscription.service';
import { MentionSearchService } from '../../../Monitor/Core/services/mention-search.service';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { MentionsService } from '../../../Monitor/Core/services/mentions.service';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { MonitoringRoutingService } from '../../../Monitor/Core/services/monitoring-routing.service';
import { SearchResultsService } from '../../../Monitor/Core/services/search-results.service';
import { TagsService } from '../../../Common/Tags/services/tags.service';
import { TargetingRhpContextsService } from '../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { TargetingRhpDialogService } from '../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import { TargetingSearchService } from '../../../Discover/Targeting/services/targeting-search.service';
import { Title } from '@angular/platform-browser';
import { ToolbarStateService } from '../../../Common/TargetingShared/services/toolbar-state.service';
import { TopicBackfillApi } from '../../../api/backfills/topic-backfill.api';
import { TopicsService } from '../../../Monitor/Core/services/topics.service';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { WebsocketAwareNeptuneService } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { MentionTargetingRhpContextsService }
    from '../../../Common/RhpContexts/services/mention-targeting-rhp-contexts.service';
import { MonitoringResultComponent } from '../../../Monitor/Shared/components/MonitoringResult/monitoring-result.component';
import { ManageSourceAgentsService } from '../../services/manage-source-agents.service';

/**
 * The agent's mention feed: the folder/topic results view, scoped to the mentions this agent has
 * acted on. Extends MonitoringResultComponent so the toolbar, the list and the right hand panel are
 * the shared ones rather than copies - only the heading and the view mode tabs are dropped, and the
 * starting filters come from AgentMentionsFilterResolver.
 */
@Component({
    selector: 'agpr-agent-mentions',
    templateUrl: './agent-mentions.component.html',
    styleUrls: ['./agent-mentions.component.scss'],
    // A subclass does not inherit its parent's decorator, so the results page's own providers have
    // to be restated here. Keep in step with MonitoringResultComponent.
    providers: [
        ToolbarStateService,
        {
            provide: 'PaginatorServiceInterface',
            useExisting: MentionSearchService
        },
        {
            provide: TargetingRhpContextsService,
            useClass: MentionTargetingRhpContextsService
        },
        TargetingRhpDialogService
    ],
    standalone: false
})
export class AgentMentionsComponent extends MonitoringResultComponent implements OnInit {

    /** The window last taken from the shared range, so its echo is not sent back out */
    private adoptedRange: DateQueryModel;

    /** The agent this feed belongs to, for the summary tab */
    agent: CurationAgentModel;

    constructor (

        public monitoringRoutingService: MonitoringRoutingService,
        public topicsService: TopicsService,
        public mentionSearchService: MentionSearchService,
        public searchResultsService: SearchResultsService,
        public folderService: FolderService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public tagsService: TagsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public mentionsService: MentionsService,
        public monitorConfigService: MonitorConfigService,
        public route: ActivatedRoute,
        public currentUserStateService: CurrentUserStateService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public targetingRhpDialogService: TargetingRhpDialogService,
        public userPreferencesService: UserPreferencesService,
        public currencyConversionService: CurrencyConversionService,
        public titleService: Title,
        public targetingSearchService: TargetingSearchService,
        public injector: Injector,
        public websocketEvents: WebsocketAwareNeptuneService,
        public topicBackfillApi: TopicBackfillApi,
        public configService: ConfigService,
        public toolbarStateService: ToolbarStateService,
        public mentionsApi: MentionsApi,
        public curationAgentsService: CurationAgentsService,
        public manageSourceAgentsService: ManageSourceAgentsService,
    ) {
        super(
            monitoringRoutingService,
            topicsService,
            mentionSearchService,
            searchResultsService,
            folderService,
            localizationService,
            coreDialogService,
            tagsService,
            mediaSubscriptionService,
            mentionsService,
            monitorConfigService,
            route,
            currentUserStateService,
            targetingRhpContextsService,
            targetingRhpDialogService,
            userPreferencesService,
            currencyConversionService,
            titleService,
            targetingSearchService,
            injector,
            websocketEvents,
            topicBackfillApi,
            configService,
            toolbarStateService,
            mentionsApi,
            manageSourceAgentsService
        );
    }

    override ngOnInit(): void {
        // The agent feed is not a Monitoring route, so setActiveDateFilterOptions() bails out and the
        // toolbar keeps its defaults - which carry a "Select ..." entry with a null value that the
        // date popup cannot read. Saved coverage options, the same as the folder results use.
        this.dateFilterOptions = getMonitoringDateQueryInputFolderOptions();

        // The routing service may have been constructed by this very component, after the
        // NavigationEnd it listens for, so ask it to publish the current URL before anything reads it
        this.monitoringRoutingService.syncCurrentRoute();

        // The sidebar already narrows the feed by source, so the Agents filter would only repeat it
        this.mentionSearchService.hideAiAgentsFilter = true;

        super.ngOnInit();

        // Agents act on saved coverage, so the feed has to search the account - /saved-mentions/search
        // - rather than new coverage. The base builds its MentionSearchModel inside an async
        // subscription and keeps only the resolved query, so setting the flag straight after
        // super.ngOnInit() is too early: this.search does not exist yet. Watch the criteria instead
        // and turn it on whenever one arrives without it; re-pushing settles immediately, since the
        // next emission already carries the flag.
        this.subscriptions.push(
            this.mentionSearchService.getSearchCriteria().pipe(
                filter(criteria => !!criteria && !criteria.isAccountSearch)
            ).subscribe(criteria => {
                criteria.isAccountSearch = true;

                if (this.search?.searchCriteria) {
                    this.search.searchCriteria.isAccountSearch = true;
                }

                this.mentionSearchService.setSearchCriteria(criteria);
                this.mentionSearchService.applyFilters(false);
                this.mentionSearchService.search(true, true);
            })
        );

        // The summary tab describes the agent, so load it alongside its mentions
        this.subscriptions.push(
            this.route.parent.paramMap.pipe(
                map(params => Number(params.get('agent_id'))),
                filter(id => !!id),
                switchMap(id => this.curationAgentsService.get(id).pipe(catchError(() => of(null)))),
                filter(agent => !!agent)
            ).subscribe(agent => this.agent = agent)
        );

        // The toolbar's date picker belongs to the <agpr-mention-search-result> child, so its
        // (ngModelChange) calls that component's dateRangeChanged, never this one. Watch the search
        // service instead - it is where both of them end up - and forward to the shared window,
        // skipping whatever this component just adopted so the two do not bounce off each other.
        this.subscriptions.push(
            this.mentionSearchService.getDateQuery().pipe(
                filter(dateQuery => !!dateQuery && !DateQueryModel.equals(dateQuery, this.adoptedRange, false))
            ).subscribe(dateQuery => this.curationAgentsService.setSelectedDateRange(dateQuery))
        );

        // The dashboard and the feed report over one window, so the feed opens on whatever range was
        // last picked and hands its own changes back.
        this.subscriptions.push(
            this.curationAgentsService.selectedDateRange$.subscribe(dateRange => {
                if (!dateRange || DateQueryModel.equals(dateRange, this.startDateRange, false)) {
                    return;
                }

                this.adoptedRange = dateRange;
                this.startDateRange = dateRange;
                this.dateRangeChanged(dateRange);
            })
        );

        // Everything the feed is scoped by: the agent itself, and whatever the left hand panel has
        // ticked. The criteria is in here too because the scope has to be stamped on the one the
        // search service holds - the component's own copy is not what gets posted - and the base
        // replaces that criteria more than once while the page settles.
        this.subscriptions.push(
            combineLatest([
                this.route.parent.paramMap.pipe(
                    map(params => Number(params.get('agent_id'))),
                    filter(agentId => !!agentId)
                ),
                this.curationAgentsService.agentScope$,
                this.mentionSearchService.getSearchCriteria().pipe(filter(criteria => !!criteria))
            ]).subscribe(
                ([agentId, agentScope, criteria]) => this.applyAgentScope(agentId, agentScope, criteria)
            )
        );
    }

    /**
     * Scopes the feed to the agent and to whatever the sidebar has ticked, on the criteria the
     * search service posts - the component's own copy is not what goes to the API.
     *
     * Re-pushing the criteria re-emits it, so this settles rather than looping: the second pass
     * finds the scope already applied and stops there.
     */
    private applyAgentScope(agentId: number, agentScope: AgentScopeModel, criteria: MentionSearchCriteriaModel): void {
        if (!applyAgentScope(criteria.query, agentId, agentScope)) {
            return;
        }

        // Kept in step so anything reading the component's own copy sees the same scope
        if (this.search?.searchCriteria?.query) {
            applyAgentScope(this.search.searchCriteria.query, agentId, agentScope);
        }

        // The flag decides which filter list the panel offers, so it goes with every push
        criteria.isAccountSearch = true;

        this.mentionSearchService.setSearchCriteria(criteria);
        this.mentionSearchService.applyFilters(false);
        this.mentionSearchService.search(true, true);
    }

    /** Applies a window taken from the shared range */
    override dateRangeChanged(dateRange: DateQueryModel): void {
        super.dateRangeChanged(dateRange);
    }
}
